import {computed, inject, Injectable, signal} from '@angular/core';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {CreateFolderRequest, DocumentService, DocumentSummary, FolderService, FolderSummary, MoveDocumentFolderRequest, UpdateFolderRequest} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';

export interface MoveFolderInput extends UpdateFolderRequest {
  id: number;
}

export interface UploadDocumentInput {
  file: File;
  folderId?: number | null;
}

@Injectable()
export class DocumentStore {
  private readonly folderService = inject(FolderService);
  private readonly documentService = inject(DocumentService);
  private readonly queryClient = inject(QueryClient);

  selectedFolderId = signal<number | null>(null);

  foldersQuery = injectQuery(() => ({
    queryKey: [QueryKey.FOLDERS],
    queryFn: () => lastValueFrom(this.folderService.getFolders()),
  }));

  documentsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DOCUMENTS, this.selectedFolderId()],
    queryFn: () => lastValueFrom(this.documentService.getDocuments(this.selectedFolderId()!)),
    enabled: this.selectedFolderId() !== null,
  }));

  folders = computed<FolderSummary[]>(() => this.foldersQuery.data()?.folders ?? []);
  documents = computed<DocumentSummary[]>(() => this.documentsQuery.data()?.documents ?? []);
  isLoadingFolders = computed(() => this.foldersQuery.isLoading());
  isLoadingDocuments = computed(() => this.documentsQuery.isLoading());

  allFolders = computed<FolderSummary[]>(() => this.flattenFolders(this.folders()));

  selectedFolder = computed(() =>
    this.allFolders().find(f => f.id === this.selectedFolderId()) ?? null
  );

  private flattenFolders(folders: FolderSummary[]): FolderSummary[] {
    return folders.flatMap(f => [f, ...this.flattenFolders(f.folders)]);
  }

  createFolderMutation = injectMutation(() => ({
    mutationFn: (req: CreateFolderRequest) =>
      lastValueFrom(this.folderService.createFolder(req)),
    onSuccess: () => this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]}),
  }));

  moveFolderMutation = injectMutation(() => ({
    mutationFn: (req: MoveFolderInput) =>
      lastValueFrom(this.folderService.updateFolder(req.id, {
        name: req.name,
        parentId: req.parentId,
        orderNum: req.orderNum,
        userIds: req.userIds,
      })),
    onSuccess: () => this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]}),
  }));

  deleteFolderMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.folderService.deleteFolder(id)),
    onSuccess: (_, id) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      if (this.selectedFolderId() === id) this.selectedFolderId.set(null);
    },
  }));

  uploadDocumentMutation = injectMutation(() => ({
    mutationFn: (req: UploadDocumentInput) =>
      lastValueFrom(this.documentService.uploadDocument(req.file, req.folderId)),
    onSuccess: () => this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]}),
  }));

  moveDocumentFolderMutation = injectMutation(() => ({
    mutationFn: (req: MoveDocumentFolderRequest) =>
      lastValueFrom(this.documentService.moveDocumentFolder(req)),
    onSuccess: () => this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]}),
  }));

  deleteDocumentMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.documentService.deleteDocument(id)),
    onSuccess: () => this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]}),
  }));

  downloadDocument(id: number) {
    return this.documentService.downloadDocument(id);
  }
}
